import React from 'react';
import { Link } from 'react-router-dom';

import { Text } from '~/components/core';
import Tooltip from '~/components/core/Atomic/Tooltip';
import cn from '~/Utils/cn';

import { useMiniDrawer } from './MiniDrawerContext';
import type { MiniDrawerItemStandaloneProps } from './types';

const MiniDrawerItem: React.FC<MiniDrawerItemStandaloneProps> = ({
  itemKey,
  Icon,
  label,
  onClick,
  disabled,
  to,
  href,
  className,
  selectedClassName,
}) => {
  const { isExpanded, iconSize, selectedKey, onItemSelect, itemClassName, selectedItemClassName } = useMiniDrawer();

  const isSelected = selectedKey === itemKey;

  const baseClassName = cn(
    'flex w-full items-center py-12 transition-colors',
    { 'cursor-not-allowed opacity-50': disabled },
    itemClassName,
    className
  );

  const selectedClassNameCombined = cn(selectedItemClassName, selectedClassName);

  const combinedClassName = cn(baseClassName, {
    [selectedClassNameCombined]: isSelected && selectedClassNameCombined,
  });

  const handleClick = () => {
    if (disabled) return;
    if (onClick) {
      onClick();
    } else {
      onItemSelect?.(itemKey);
    }
  };

  const content = (
    <>
      <Icon size={iconSize} className="flex-shrink-0 items-center" iconColor="currentColor" />
      <Text
        variant={Text.VARIANTS.SM}
        weight={Text.WEIGHTS.REGULAR}
        className={cn('ml-16 whitespace-nowrap transition-opacity duration-200', {
          'hidden overflow-hidden': !isExpanded,
          'opacity-100': isExpanded,
        })}
      >
        {label}
      </Text>
    </>
  );

  const renderContent = () => {
    if (to && !disabled) {
      return (
        <Link to={to} className={combinedClassName} onClick={handleClick}>
          {content}
        </Link>
      );
    }

    if (href && !disabled) {
      return (
        <a href={href} className={combinedClassName} onClick={handleClick} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      );
    }

    return (
      <button type="button" className={combinedClassName} onClick={handleClick} disabled={disabled}>
        {content}
      </button>
    );
  };

  return (
    <Tooltip title={label} placement="right" disabled={isExpanded}>
      <div className="inline-flex w-full">{renderContent()}</div>
    </Tooltip>
  );
};

export default MiniDrawerItem;
